import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { changeEnterHTML } from 'src/utils/changeEnter';
import { getFontURL } from 'src/utils/getFontUrl';
import { trackingList } from 'src/utils/valueHTML';

const BasicBottomSheetHTML: FC = () => {
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const radius = useSelector((state: RootState) => state.imageUpload.radius);
  const deeplink = useSelector(
    (state: RootState) => state.imageUpload.deeplink
  );
  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );
  const btnAgain = useSelector((state: RootState) => state.buttons.btnAgain);

  const tracking = useSelector((state: RootState) => state.trackings.tracking);
  const button1 = useSelector((state: RootState) => state.trackings.button1);
  const button2 = useSelector((state: RootState) => state.trackings.button2);

  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.3;

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(btnFontFamily?.button);
  const neverShowBtnFontURL = getFontURL(btnFontFamily?.never_show_button);

  const style = `
    @font-face {
      font-family: "title";
      src: url(${titleFontURL});
    }
    @font-face {
      font-family: "subTitle";
      src: url(${subTitleFontURL});
    }
    @font-face {
      font-family: "btn";
      src: url(${btnFontURL});
    }
    @font-face {
      font-family: "neverShowBtn";
      src: url(${neverShowBtnFontURL});
    }
     /* reset */
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      list-style: none;
      text-decoration: none;      
    }
    
    html,
    body {
      height: 100%;
      overflow: hidden;
      font-size: 16px;      
    }
    main {
      height: 100%;
      position: relative;
    }
    a {
      color: #000000;
      cursor: pointer;
    }
    button {
      background: none;
      border: none;
    }

    .root {
      width: 100%;
      height: 100%;
      position: relative;
      background: none;
      overflow: hidden;
      display: flex;
      align-items: flex-end;
    }

    .background {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      background: rgba(0, 0, 0, ${displayValue});
    }

    .outer-wrapper {
      position: relative;
      width: 100%;
      height: fit-content;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .modal-wrapper {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: auto;
      border-radius: ${radius?.background}px;
      padding: 36px 15px 24px 15px;
      background-color: ${bgColor?.background};
      gap: 16px;
    }

    .contents-box {
      width: 100%;
      height: auto;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .contents-box .title,
    .contents-box .sub-title {
      width: 100%;
      min-height: 48px;
      display: flex;
      align-items: center;
    }

    .title p {
      width: 100%;
      font-size: ${fontSize?.title}rem;
      text-align: ${textAlign?.title};
      font-weight: ${fontWeight?.title};
      color: ${color?.title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily.title
          : 'title'
      };
    }

    .sub-title p {
      width: 100%;
      font-size: ${fontSize?.sub_title}rem;
      font-weight: ${fontWeight?.sub_title};
      line-height: 1.35;
      text-align: ${textAlign?.sub_title};
      color: ${color?.sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily.sub_title
          : 'subTitle'
      };
    }

    .btn-wrapper {
      width: 100%;
      height: auto;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .btn-inner {
      width: 100%;
      height: 52px;
      font-size: ${btnFontSize?.button}rem;
      font-weight: ${btnFontWeight?.button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.button
        )
          ? btnFontFamily.button
          : 'btn'
      };
      line-height: 52px;
      color: ${btnColor?.button};
      background-color: rgba(${btnBgColorR?.button}, ${btnBgColorG?.button}, ${
    btnBgColorB?.button
  }, ${btnBgColorA?.button});
      border-radius: ${radius?.button}px;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 5px;
      border: none;
    }

    .btn-close {
      width: 100%;
      height: 52px;
      font-size: ${btnFontSize?.never_show_button}rem;
      font-weight: ${btnFontWeight?.never_show_button};
      line-height: 52px;
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.never_show_button
        )
          ? btnFontFamily.never_show_button
          : 'neverShowBtn'
      };
      color: ${btnColor?.never_show_button};
      background-color: rgba(${btnBgColorR?.never_show_button}, ${
    btnBgColorG?.never_show_button
  }, ${btnBgColorB?.never_show_button}, ${btnBgColorA?.never_show_button});
      text-align: center;
      border-radius: ${radius?.never_show_button}px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 5px;
    }

    @media screen and (min-width: 391px) {
      .modal-wrapper {
        width: 390px;
        height: auto;
      }
    }
`;
  const deeplink_list = JSON.stringify(deeplink?.background);

  const tracking_list = JSON.stringify([
    tracking?.button?.[0],
    tracking?.never_show_button?.[0]
  ]);
  const tracking_button_list = trackingList(
    'BASIC BOTTOM SHEET',
    button1,
    button2
  );
  const html = `
    <div class="root">
        <div class="background" onclick="appboyBridge.logClick('background'); appboyBridge.closeMessage();"></div>
        <div class="outer-wrapper">
            <div class="modal-wrapper">
                <div class="contents-box">
                    <div class="title">
                        <p>${changeEnterHTML(text?.title)}</p>
                    </div>
                    <div class="sub-title">
                        <p>${changeEnterHTML(text?.sub_title)}</p>
                    </div>
                </div>
                <div class="btn-wrapper">
                    <button class="btn-inner">${btnText?.button}</button>
                    <div class="btn-close">${btnText?.never_show_button}</div>
                </div>
            </div>
        </div>
        <div id="deeplink" style="display:none;">
            ${deeplink_list}
        </div>
    </div>
  `;

  const js = `
    document.addEventListener("DOMContentLoaded", function(){
        let content = document.getElementById("deeplink").textContent;
        let link = content.replace(/\\n|\\t/g, "").trim();
        let deeplink = JSON.parse(link);

        let tracking = ${tracking_list};
        let button1 = ${tracking_button_list[0]};
        let button2 = ${tracking_button_list[1]};       

        document.querySelector('.btn-inner').addEventListener('click', function() {
            try {
                appboyBridge.logClick(tracking[0]);
                if(button1[0].indexOf(0) !== -1) {
                    appboyBridge.logClick('0');
                }
                if(button2[0].indexOf(0) !== -1) {
                    appboyBridge.logClick('1');
                }
            } catch (error) {}
            window.location = deeplink;
        })

        document.querySelector('.btn-close').addEventListener('click', function() {
            try {
                appboyBridge.logClick(tracking[1]);
                if(button1[1].indexOf(0) !== -1) {
                    appboyBridge.logClick('0');
                }
                if(button2[1].indexOf(0) !== -1) {
                    appboyBridge.logClick('1');
                }
                if ("${btnAgain?.type}" === "attribute") {
                    appboyBridge.getUser().setCustomUserAttribute("${btnAgain?.never_show_button}", new Date());
                }
                if ("${btnAgain?.type}" === "event") {
                    appboyBridge.logCustomEvent("${btnAgain?.never_show_button}");
                }
            } catch (error) {}
            appboyBridge.closeMessage();            
        })
    })`;

  return (
    <>
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          httpEquiv="Cache-Control"
          content="no-cache, no-store, must-revalidate"
        />
        <meta httpEquiv="Expires" content="0" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          as="style"
          href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdn.jsdelivr.net/npm/normalize.css@8.0.1/normalize.css"
        />
        <style dangerouslySetInnerHTML={{ __html: style }} />
        <script
          dangerouslySetInnerHTML={{
            __html: `               
            (function () {
              function adjustFontSize() {
                var actualSize = Math.round(parseFloat(window.getComputedStyle(document.documentElement).fontSize));
                if (actualSize !== 16) {              
                  var newFontSize = (16 * 16) / actualSize;
                  document.documentElement.style.fontSize = newFontSize + "px";                            
                }
              }                        
              adjustFontSize();
            })();                           
          `
          }}
        />
      </head>
      <body dangerouslySetInnerHTML={{ __html: html }} />
      <script dangerouslySetInnerHTML={{ __html: js }} />
    </>
  );
};

export default BasicBottomSheetHTML;
